import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Search,
  Filter,
  ArrowRight,
  MapPin,
  Sparkles,
  CheckCircle2,
  Building2,
  UserCircle2,
  Loader2,
  X,
} from "lucide-react";
import Header from "@/components/politics/Header";
import Footer from "@/components/politics/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_LABEL = {
  founding: { text: "Fundador", color: "#7E20FF" },
  certified: { text: "Certificado", color: "#208BFF" },
  operator: { text: "Operador RSM", color: "#3ED383" },
};

const TYPE_LABEL = {
  agencia: { text: "Agência", icon: Building2 },
  consultor: { text: "Consultor", icon: UserCircle2 },
};

export default function PartnersDirectory() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/partners`, { params: { limit: 200 } })
      .then((res) => {
        if (mounted) setPartners(res.data || []);
      })
      .catch(() => {
        if (mounted) setError("Não foi possível carregar o diretório agora.");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const states = useMemo(() => {
    return Array.from(new Set(partners.map((p) => p.state).filter(Boolean))).sort();
  }, [partners]);

  const filtered = useMemo(() => {
    return partners.filter((p) => {
      if (typeFilter !== "all" && p.type !== typeFilter) return false;
      if (stateFilter !== "all" && p.state !== stateFilter) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = [
          p.name,
          p.tagline,
          p.description,
          p.city,
          p.state,
          ...(p.specialties || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [partners, query, typeFilter, stateFilter]);

  const reset = () => {
    setQuery("");
    setTypeFilter("all");
    setStateFilter("all");
  };

  return (
    <main
      data-testid="partners-directory-page"
      className="min-h-screen bg-[#F5FAFB] text-[#1B1D1D]"
    >
      <Header />

      {/* Hero */}
      <section
        data-testid="partners-hero"
        className="relative isolate overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20"
      >
        <div className="absolute inset-0 -z-10 rsm-grid-bg opacity-90" />
        <div
          className="absolute -top-32 right-[-10%] -z-10 h-[460px] w-[460px] rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(closest-side, #7E20FF, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-32 left-[-10%] -z-10 h-[420px] w-[420px] rounded-full blur-3xl opacity-25"
          style={{ background: "radial-gradient(closest-side, #208BFF, transparent 70%)" }}
        />

        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-4xl rsm-rise">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#1B1D1D]/10 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-[#1B1D1D]">
              <Sparkles size={12} className="text-[#7E20FF]" />
              Diretório · /politics/parceiros
            </div>
            <h1
              data-testid="partners-headline"
              className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.94] text-[#1B1D1D]"
            >
              Parceiros{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#7E20FF]">RSM Politics</span>
                <span className="absolute inset-x-0 bottom-1 h-3 -z-0 bg-[#7E20FF]/15 rounded" />
              </span>
            </h1>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-[#51565F]">
              Agências e consultores certificados para operar programas de base,
              inteligência de campo e leitura contínua de dados nas campanhas de
              2026. Diretório em formação — primeira leva sendo certificada.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/politics/agencies#parceria"
                data-testid="partners-cta-join"
                className="inline-flex items-center gap-2 rounded-full bg-[#7E20FF] px-6 py-3 text-sm font-semibold text-white transition-all duration-300 ease-out hover:bg-[#6818D6] hover:-translate-y-0.5"
              >
                Quero ser parceiro
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/admin/login"
                data-testid="partners-cta-partner-login"
                className="inline-flex items-center gap-2 rounded-full border border-[#1B1D1D]/15 bg-white/70 backdrop-blur px-6 py-3 text-sm font-medium text-[#1B1D1D] hover:border-[#208BFF] hover:text-[#208BFF] transition-colors"
              >
                Acesso do parceiro
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <section
        data-testid="partners-toolbar"
        className="relative border-y border-[#1B1D1D]/8 bg-white/70 backdrop-blur sticky top-16 z-30"
      >
        <div className="mx-auto max-w-7xl px-6 py-4 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7D8188]"
            />
            <input
              data-testid="partners-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome, cidade, especialidade…"
              className="w-full rounded-full border border-[#1B1D1D]/12 bg-white pl-11 pr-4 py-2.5 text-sm focus:border-[#7E20FF] focus:outline-none focus:ring-2 focus:ring-[#7E20FF]/20 transition-all"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-[#7D8188]" />
            <FilterChip
              testid="filter-type-all"
              active={typeFilter === "all"}
              onClick={() => setTypeFilter("all")}
            >
              Todos
            </FilterChip>
            <FilterChip
              testid="filter-type-agencia"
              active={typeFilter === "agencia"}
              onClick={() => setTypeFilter("agencia")}
            >
              Agências
            </FilterChip>
            <FilterChip
              testid="filter-type-consultor"
              active={typeFilter === "consultor"}
              onClick={() => setTypeFilter("consultor")}
            >
              Consultores
            </FilterChip>

            <select
              data-testid="filter-state"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="rounded-full border border-[#1B1D1D]/12 bg-white px-3.5 py-1.5 text-xs font-semibold tracking-wide uppercase text-[#1B1D1D] focus:border-[#7E20FF] focus:outline-none"
            >
              <option value="all">Todos os estados</option>
              {states.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            {(query || typeFilter !== "all" || stateFilter !== "all") && (
              <button
                data-testid="filter-reset"
                onClick={reset}
                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium text-[#7E20FF] hover:bg-[#7E20FF]/8 transition-colors"
              >
                <X size={12} /> limpar
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section
        data-testid="partners-grid-section"
        className="relative py-16 sm:py-20"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <p
              data-testid="partners-count"
              className="text-sm text-[#51565F]"
            >
              {loading
                ? "Carregando parceiros…"
                : `${filtered.length} ${filtered.length === 1 ? "parceiro" : "parceiros"} no diretório`}
            </p>
            <span className="text-[11px] tracking-wider uppercase text-[#7D8188]">
              Diretório em formação
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-[#7D8188]">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-[#D94A0A]/30 bg-[#D94A0A]/5 p-8 text-center text-[#D94A0A]">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState onReset={reset} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((p, i) => (
                <PartnerCard key={p.slug} partner={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA strip */}
      <section className="relative py-20 sm:py-24 bg-white border-t border-[#1B1D1D]/8">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Você é agência ou consultor?
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tighter text-[#1B1D1D]">
            Entre na primeira leva de parceiros certificados.
          </h2>
          <p className="mt-4 text-[#51565F] max-w-2xl mx-auto">
            O programa segue em formalização. Quem entra agora ajuda a desenhar o
            padrão e participa do diretório com prioridade.
          </p>
          <Link
            to="/politics/agencies#parceria"
            data-testid="partners-bottom-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#7E20FF] px-7 py-4 text-base font-semibold text-white hover:bg-[#6818D6] hover:-translate-y-0.5 transition-all duration-300 ease-out"
          >
            Quero ser parceiro RSM Politics
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wide uppercase transition-all duration-200 ${
        active
          ? "bg-[#1B1D1D] text-white"
          : "bg-white text-[#51565F] border border-[#1B1D1D]/12 hover:border-[#1B1D1D]/30"
      }`}
    >
      {children}
    </button>
  );
}

function PartnerCard({ partner, index }) {
  const tier = TIER_LABEL[partner.tier] || TIER_LABEL.certified;
  const typeInfo = TYPE_LABEL[partner.type] || TYPE_LABEL.agencia;
  const TypeIcon = typeInfo.icon;
  const initials = partner.name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Link
      to={`/politics/parceiros/${partner.slug}`}
      data-testid={`partner-card-${index}`}
      className="group relative flex flex-col rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#7E20FF]/30"
    >
      {partner.featured && (
        <span className="absolute top-4 right-4 text-[10px] tracking-[0.2em] uppercase font-bold text-[#FA9F22]">
          Destaque
        </span>
      )}

      <div className="flex items-start gap-4">
        {partner.logo_url ? (
          <img
            src={partner.logo_url}
            alt={`${partner.name} logo`}
            className="h-14 w-14 rounded-xl object-contain bg-[#F5FAFB] p-2 border border-[#1B1D1D]/8"
          />
        ) : (
          <div
            className="grid h-14 w-14 place-items-center rounded-xl bg-[#F5FAFB] border border-[#1B1D1D]/8 font-display text-base font-bold"
            style={{ color: tier.color }}
          >
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <TypeIcon size={13} className="text-[#7D8188]" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#7D8188]">
              {typeInfo.text}
            </span>
          </div>
          <h3 className="mt-1 font-display text-lg font-semibold leading-tight text-[#1B1D1D] truncate">
            {partner.name}
          </h3>
          <span
            className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] uppercase"
            style={{ color: tier.color }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: tier.color }}
            />
            {tier.text}
          </span>
        </div>
      </div>

      {partner.tagline && (
        <p className="mt-5 text-[14px] leading-relaxed text-[#51565F] line-clamp-3">
          {partner.tagline}
        </p>
      )}

      <div className="mt-auto pt-5 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs text-[#7D8188]">
          <MapPin size={12} />
          {[partner.city, partner.state].filter(Boolean).join(" · ") || "—"}
        </span>
        <AvailabilityBadge
          availability={partner.availability}
          testid={`partner-card-availability-${index}`}
        />
      </div>

      {partner.specialties?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {partner.specialties.slice(0, 3).map((s) => (
            <span
              key={s}
              className="rounded-full bg-[#F5FAFB] px-2.5 py-1 text-[10px] font-medium text-[#51565F] border border-[#1B1D1D]/6"
            >
              {s}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#7E20FF] group-hover:gap-2 transition-all">
          Ver perfil
          <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}

function EmptyState({ onReset }) {
  return (
    <div
      data-testid="partners-empty"
      className="rounded-2xl border border-dashed border-[#1B1D1D]/15 bg-white/60 p-12 text-center"
    >
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[#7E20FF]/10 text-[#7E20FF]">
        <Search size={20} />
      </div>
      <h3 className="mt-4 font-display text-xl font-semibold text-[#1B1D1D]">
        Nenhum parceiro encontrado com esses filtros.
      </h3>
      <p className="mt-2 text-sm text-[#51565F] max-w-md mx-auto">
        Tente ajustar a busca ou ver todo o diretório. O programa está em
        formação — novos parceiros são certificados continuamente.
      </p>
      <button
        onClick={onReset}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1B1D1D] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#7E20FF] transition-colors"
      >
        <CheckCircle2 size={14} /> Limpar filtros
      </button>
    </div>
  );
}


function AvailabilityBadge({ availability, testid }) {
  const accepting = (availability || "accepting") === "accepting";
  const color = accepting ? "#3ED383" : "#7D8188";
  const text = accepting ? "Aceitando projetos" : "Lotado";
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9.5px] font-bold tracking-[0.16em] uppercase"
      style={{ color, backgroundColor: `${color}15` }}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${accepting ? "animate-pulse" : ""}`}
        style={{ background: color }}
      />
      {text}
    </span>
  );
}
